// Approval code (owner, Oct 29 2026 - docs/APPROVAL_CODES_2026-10-29.md): a command the
// owner switched on answers 403 approval_required for anyone but an owner / manager;
// this opens. The approver picks their name and enters THEIR OWN approval code (not
// their POS PIN); the 5-minute single-use token goes back on the same command.
// Fallback: the owner's / manager's account e-mail + password. "Call a manager"
// sends an approval request alert.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, errorText } from '../lib/api';
import { C } from '../theme/tokens';
import { Btn, ErrorNote, Field, inputStyle, Modal, ModalFoot, ModalHead, Segmented } from './ui';
import { IconBackspace, IconCheck, IconShield } from './icons';

export const ACTION_LABEL = {
  'payments.refund': 'Refund a payment',
  'payments.void': 'Void a card payment',
  'items.void': 'Void a sent item',
  'orders.modify_sent': 'Change a sent item',
  'orders.cancel': 'Cancel an order',
  'discounts.apply': 'Discount or comp',
  'checks.service_charge': 'Service charge',
  'kitchen.clear_old': 'Clear old kitchen tickets',
  'timeclock.edit': 'Edit time-clock punches',
  'settlement.batch_out': 'Batch out',
  'cash.paid_out': 'Pay cash out of the drawer',
  'cash.close_drawer': 'Close a cash drawer',
};

function PinPad({ value, onChange, onSubmit }) {
  const press = (k) => {
    if (k === 'back') onChange(value.slice(0, -1));
    else if (k === 'clear') onChange('');
    else if (value.length < 6) onChange(value + k);
  };
  useEffect(() => {
    const key = (e) => {
      if (/^\d$/.test(e.key)) press(e.key);
      else if (e.key === 'Backspace') press('back');
      else if (e.key === 'Enter' && value.length >= 4) onSubmit();
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
      <div aria-label={`${value.length} digits entered`} style={{ display: 'flex', gap: 12, height: 22, alignItems: 'center' }}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <span key={i} style={{
            width: i < 4 || value.length > i ? 16 : 10, height: i < 4 || value.length > i ? 16 : 10, borderRadius: '50%',
            background: value.length > i ? C.ink : 'transparent', border: `2px solid ${value.length > i ? C.ink : '#D6D3D1'}`,
            opacity: i >= 4 && value.length <= i ? 0.4 : 1,
          }} />
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 84px)', gap: 8 }}>
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'back'].map((k) => (
          <button key={k} type="button" className="c29-press c29-tight" onClick={() => press(k)} aria-label={k === 'back' ? 'Delete' : k === 'clear' ? 'Clear' : k}
            style={{ height: 60, borderRadius: 14, border: 'none', background: k.length > 1 ? C.sand : '#FFFFFF', boxShadow: '0 1px 0 #E7E0D8, inset 0 0 0 1px #EEE8E1', color: C.ink, fontSize: k.length > 1 ? 14 : 24, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {k === 'back' ? <IconBackspace size={22} /> : k === 'clear' ? 'Clear' : k}
          </button>
        ))}
      </div>
    </div>
  );
}

function codeError(e) {
  const code = e?.code;
  if (code === 'approval_code_wrong' || code === 'approval_code_locked' || code === 'no_approval_code'
    || code === 'approval_password_wrong') return e.message;
  return errorText(e);
}

export default function ApprovalDialog({ request, onDone, client = api, inline = false }) {
  const open = !!request;
  const [mode, setMode] = useState('code');
  const [approvers, setApprovers] = useState(null);
  const [approverError, setApproverError] = useState(null);
  const [person, setPerson] = useState(null);
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(null);

  const load = useCallback(() => {
    if (!request) return;
    setApproverError(null);
    client.get('/dining/approvers', { action: request.action }).then((d) => {
      const list = d?.approvers || [];
      setApprovers(list);
      const usable = list.filter((a) => a.has_code && !a.locked);
      setPerson((cur) => (usable.some((a) => a.person === cur) ? cur : usable[0]?.person ?? null));
    }).catch((e) => { setApprovers([]); setApproverError(e); });
  }, [request, client]);

  useEffect(() => {
    if (!open) return;
    setMode('code'); setCode(''); setPassword(''); setError(null); setSent(null); setReason(request?.reason || '');
    load();
  }, [open, request, load]);

  const approve = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { action: request.action, entity_id: request.entityId ?? null };
      if (mode === 'code') Object.assign(body, { approver: person, approval_code: code });
      else Object.assign(body, { manager_email: email.trim(), manager_password: password });
      const answer = await client.post('/dining/approvals', body);
      onDone({ token: answer.approval_token, approver: answer.approver });
    } catch (e) {
      setCode('');
      setError(codeError(e));
      if (e?.code === 'approval_code_locked') load();
    } finally {
      setBusy(false);
    }
  };

  const callManager = async () => {
    setBusy(true);
    setError(null);
    try {
      const answer = await client.post('/dining/approvals/request', {
        action: request.action, entity_id: request.entityId ?? null, session_id: request.sessionId ?? null,
        reason: reason.trim() || null,
      });
      setSent(answer?.repeated ? 'already' : 'sent');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  const withCode = (approvers || []).filter((a) => a.has_code);
  const canApprove = mode === 'code' ? !!person && code.length >= 4 : !!email.trim() && !!password;
  const Frame = inline ? InlineFrame : Modal;
  return (
    <Frame open={open} onClose={() => onDone(null)} label="Approval code" width={560}>
      <ModalHead title="Approval code needed" onClose={() => onDone(null)}
        sub={`${ACTION_LABEL[request.action] || request.action}${request.what ? ` · ${request.what}` : ''} · an owner or manager approves with their own code`} />
      <div className="c29-scroll" style={{ padding: '16px 24px 18px', display: 'flex', flexDirection: 'column', gap: 16 }} data-testid="approval-dialog">
        <Segmented value={mode} onChange={(m) => { setMode(m); setError(null); }} options={[
          { value: 'code', label: 'Approval code' }, { value: 'account', label: 'Account' }, { value: 'call', label: 'Call a manager' },
        ]} />
        {mode === 'code' && (
          <>
            {approverError && <ErrorNote light error={approverError} compact />}
            {approvers && withCode.length === 0 && !approverError && (
              <div role="status" style={{ fontSize: 14, color: C.muted, lineHeight: 1.5 }} data-testid="approval-no-codes">
                No owner or manager has set an approval code yet. They set one in Back Office → Staff &amp; Labor → POS Login Access. Until then, use their account, or call a manager.
              </div>
            )}
            {approvers && approvers.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {approvers.map((a) => {
                  const on = a.person === person;
                  const usable = a.has_code && !a.locked;
                  return (
                    <button key={a.person} type="button" className="c29-press" disabled={!usable} aria-pressed={on}
                      onClick={() => { setPerson(a.person); setCode(''); setError(null); }} data-testid={`approver-${a.person}`}
                      style={{ height: 48, padding: '0 16px', borderRadius: 12, border: on ? `2px solid ${C.orange}` : `1px solid ${C.border}`, background: on ? C.orangeTint : '#FFFFFF', color: on ? '#9A3412' : usable ? C.ink2 : C.faint, fontSize: 14, fontWeight: on ? 800 : 600, display: 'inline-flex', alignItems: 'center', gap: 8, cursor: usable ? 'pointer' : 'not-allowed' }}>
                      {on && <IconCheck size={15} />}{a.name}
                      <span style={{ fontSize: 12, color: C.faint, fontWeight: 600 }}>
                        {a.locked ? 'locked' : a.has_code ? (a.role === 'owner' ? 'Owner' : 'Manager') : 'no code yet'}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {person && <PinPad value={code} onChange={setCode} onSubmit={approve} />}
          </>
        )}
        {mode === 'account' && (
          <>
            <Field label="Owner or manager e-mail"><input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} /></Field>
            <Field label="Password"><input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle}
              onKeyDown={(e) => { if (e.key === 'Enter' && canApprove) approve(); }} /></Field>
          </>
        )}
        {mode === 'call' && (
          <>
            <Field label="What is it for?" hint="A manager sees this on their screen with the table.">
              <input value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Guest sent the steak back" style={inputStyle} />
            </Field>
            {sent && (
              <div role="status" style={{ borderRadius: 12, background: C.greenSoft, color: '#14532D', padding: '12px 14px', fontSize: 14, fontWeight: 600, lineHeight: 1.45 }}>
                {sent === 'already' ? 'A manager was already asked a moment ago.' : 'Request sent.'} When they come over, they enter their approval code here.
              </div>
            )}
          </>
        )}
        {error && <ErrorNote light error={error} compact />}
      </div>
      <ModalFoot>
        <Btn kind="white" onClick={() => onDone(null)} height={56}>Cancel</Btn>
        {mode === 'call'
          ? <Btn kind="ink" height={56} busy={busy} onClick={callManager}>{sent ? 'Ask again' : 'Send request'}</Btn>
          : <Btn kind="orange" height={56} busy={busy} disabled={!canApprove} onClick={approve} icon={<IconShield size={18} />} data-testid="approval-approve">Approve</Btn>}
      </ModalFoot>
    </Frame>
  );
}

// The same dialog as a panel inside another dialog (the register's payment dialog is a
// modal of its own: a second overlay would sit under its focus trap).
function InlineFrame({ label, children }) {
  return (
    <div role="group" aria-label={label} style={{ background: '#FFFFFF', color: C.ink, borderRadius: 18, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {children}
    </div>
  );
}

// run(fn, info): fn(approvalToken?) -> Promise. A 403 approval_required asks a
// manager, then runs fn again with the token. Resolves with fn's answer, or
// null when the approval was cancelled. Other errors reject.
export function useApproval({ inline = false } = {}) {
  const [request, setRequest] = useState(null);
  const pending = useRef(null);

  const run = useCallback(async (fn, info = {}) => {
    try {
      return await fn(undefined);
    } catch (e) {
      if (!e?.isApprovalRequired) throw e;
      const approval = await new Promise((resolve) => {
        pending.current = resolve;
        setRequest({ action: e.detail?.action || info.action || e.detail?.permission, entityId: info.entityId, sessionId: info.sessionId, what: info.what, reason: info.reason });
      });
      if (!approval) return null;
      const answer = await fn(approval.token);
      return answer === undefined ? { approvedBy: approval.approver } : answer;
    }
  }, []);

  const dialog = (
    <ApprovalDialog request={request} inline={inline} onDone={(result) => {
      setRequest(null);
      const resolve = pending.current;
      pending.current = null;
      if (resolve) resolve(result);
    }} />
  );
  return { run, dialog, asking: !!request };
}
